/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useCdrSettingsData.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Custom hook for managing CDR Settings data.
    Handles both settings and fields.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  CdrSettingsData,
  CdrField,
} from '../types';
import {
  fetchCdrSettings,
  updateCdrSettings,
  updateCdrFields,
} from '../api';
import { parseDbInfoString, normalizeGeneralSettings } from '../utils';
import { DEFAULT_SETTINGS, DEFAULT_CONNECTION } from '../constants';

interface UseCdrSettingsDataOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: Error) => void;
}

const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

/**
 * Custom React hook for managing CDR Settings data.
 * Handles fetching, updating, and synchronizing both general database settings
 * and the configurable CDR field mappings.
 * 
 * @param options - Callbacks for success and error handling.
 * @returns Object containing settings data, fields list, loading states, and update handlers.
 */
export function useCdrSettingsData(options: UseCdrSettingsDataOptions = {}) {
  const { onSuccess, onError } = options;

  // Use refs to store callbacks to prevent infinite loops
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const [settingsData, setSettingsData] = useState<CdrSettingsData>(deepClone(DEFAULT_SETTINGS));
  const [originalSettings, setOriginalSettings] = useState<CdrSettingsData>(deepClone(DEFAULT_SETTINGS));
  const [fields, setFields] = useState<CdrField[]>([]);
  const [originalFields, setOriginalFields] = useState<CdrField[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSavingFields, setIsSavingFields] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Check for changes
  const hasGeneralChanges = useMemo(() => {
    const current = normalizeGeneralSettings(settingsData);
    const original = normalizeGeneralSettings(originalSettings);
    return JSON.stringify(current) !== JSON.stringify(original);
  }, [settingsData, originalSettings]);

  const normalizeFieldsForComparison = (list: CdrField[]) =>
    list
      .map((field) => ({
        id: field.id ?? field.tempId ?? '',
        varName: field.varName.trim(),
        aliasName: field.aliasName.trim(),
        quote: field.quote,
        enabled: field.enabled,
        sortOrder: field.sortOrder,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);

  const hasFieldChanges = useMemo(() => {
    const current = normalizeFieldsForComparison(fields);
    const original = normalizeFieldsForComparison(originalFields);
    return JSON.stringify(current) !== JSON.stringify(original);
  }, [fields, originalFields]);

  const hasAnyChanges = useMemo(
    () => hasGeneralChanges || hasFieldChanges,
    [hasFieldChanges, hasGeneralChanges],
  );

  // Load CDR settings
  const loadCdrSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await fetchCdrSettings();

      if (result.success && result.data) {
        const parsedConnection = parseDbInfoString(result.data.settings?.settings?.db_info);
        const mappedSettings: CdrSettingsData = {
          ...DEFAULT_CONNECTION,
          ...parsedConnection,
          dbTable: result.data.settings?.settings?.db_table ?? 'cdr',
          legs: (result.data.settings?.settings?.legs ?? 'a') as 'a' | 'b' | 'ab',
          spoolFormat: (result.data.settings?.settings?.spool_format ?? 'csv') as 'csv' | 'sql',
          rotateOnHup: Boolean(result.data.settings?.settings?.rotate_on_hup ?? true),
          debug: Boolean(result.data.settings?.settings?.debug ?? false),
        };

        setSettingsData(mappedSettings);
        setOriginalSettings(deepClone(mappedSettings));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mappedFields: CdrField[] = (result.data.fields ?? []).map((field: any) => ({
          id: field.id,
          varName: field.varName ?? field.var_name ?? '',
          aliasName: field.aliasName ?? field.alias_name ?? '',
          quote: Boolean(field.quote ?? true),
          enabled: Boolean(field.enabled ?? true),
          sortOrder: Number(field.sortOrder ?? field.sort_order ?? 0),
        }));

        setFields(mappedFields);
        setOriginalFields(deepClone(mappedFields));
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load CDR settings');
      setError(error);
      setSettingsData(deepClone(DEFAULT_SETTINGS));
      setOriginalSettings(deepClone(DEFAULT_SETTINGS));
      setFields([]);
      setOriginalFields([]);
      onErrorRef.current?.(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save general settings
  const saveGeneralSettings = useCallback(async () => {
    try {
      setIsSavingSettings(true);
      const generalSettings = normalizeGeneralSettings(settingsData);
      const payload = {
        settings: generalSettings,
      };

      const result = await updateCdrSettings(payload);

      if (result.success && result.data) {
        const parsedConnection = parseDbInfoString(result.data.settings?.db_info);
        const mappedSettings: CdrSettingsData = {
          ...DEFAULT_CONNECTION,
          ...parsedConnection,
          dbTable: result.data.settings?.db_table ?? 'cdr',
          legs: (result.data.settings?.legs ?? 'a') as 'a' | 'b' | 'ab',
          spoolFormat: (result.data.settings?.spool_format ?? 'csv') as 'csv' | 'sql',
          rotateOnHup: Boolean(result.data.settings?.rotate_on_hup ?? true),
          debug: Boolean(result.data.settings?.debug ?? false),
        };

        setSettingsData(mappedSettings);
        setOriginalSettings(deepClone(mappedSettings));
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save CDR settings');
      setError(error);
      onErrorRef.current?.(error);
      throw error;
    } finally {
      setIsSavingSettings(false);
    }
  }, [settingsData]);

  // Save field settings
  const saveFieldSettings = useCallback(async () => {
    try {
      setIsSavingFields(true);
      setError(null);

      const normalized = fields.map((field, index) => ({
        id: field.id ?? null,
        varName: field.varName.trim(),
        aliasName: field.aliasName.trim() || null,
        quote: field.quote,
        enabled: field.enabled,
        sortOrder: index + 1,
      }));

      if (normalized.some((field) => !field.varName)) {
        throw new Error('Field variable names cannot be empty');
      }

      const result = await updateCdrFields({ fields: normalized });

      if (result.success && result.data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updatedFields: CdrField[] = (result.data ?? []).map((field: any) => ({
          id: field.id,
          varName: field.varName ?? field.var_name ?? '',
          aliasName: field.aliasName ?? field.alias_name ?? '',
          quote: Boolean(field.quote ?? true),
          enabled: Boolean(field.enabled ?? true),
          sortOrder: Number(field.sortOrder ?? field.sort_order ?? 0),
        }));

        setFields(updatedFields);
        setOriginalFields(deepClone(updatedFields));
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save CDR fields');
      setError(error);
      onErrorRef.current?.(error);
      throw error;
    } finally {
      setIsSavingFields(false);
    }
  }, [fields]);

  // Save all changes
  const saveAll = useCallback(async () => {
    try {
      if (hasGeneralChanges) {
        await saveGeneralSettings();
      }
      if (hasFieldChanges) {
        await saveFieldSettings();
      }
      onSuccessRef.current?.('CDR settings saved successfully');
    } catch (err) {
      // Error already handled in individual save functions
      throw err;
    }
  }, [hasGeneralChanges, hasFieldChanges, saveGeneralSettings, saveFieldSettings]);

  // Handle settings change
  const handleSettingsChange = useCallback(<K extends keyof CdrSettingsData>(
    field: K,
    value: CdrSettingsData[K],
  ) => {
    setSettingsData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Handle field change
  const handleFieldChange = useCallback((index: number, updated: Partial<CdrField>) => {
    setFields((prev) =>
      prev.map((field, idx) => (idx === index ? { ...field, ...updated } : field)),
    );
  }, []);

  // Add field
  const handleAddField = useCallback(() => {
    setFields((prev) => {
      const nextOrder = prev.length + 1;
      return [...prev, {
        tempId: `temp-${Date.now()}-${Math.random()}`,
        varName: '',
        aliasName: '',
        quote: true,
        enabled: true,
        sortOrder: nextOrder,
      }];
    });
  }, []);

  // Remove field
  const handleRemoveField = useCallback((index: number) => {
    setFields((prev) =>
      prev
        .filter((_, idx) => idx !== index)
        .map((field, idx) => ({
          ...field,
          sortOrder: idx + 1,
        })),
    );
  }, []);

  // Handle field drag end
  const handleFieldDragEnd = useCallback((reorderedFields: CdrField[]) => {
    setFields(reorderedFields.map((field, idx) => ({
      ...field,
      sortOrder: idx + 1,
    })));
  }, []);

  // Reset to original
  const resetToOriginal = useCallback(() => {
    setSettingsData(deepClone(originalSettings));
    setFields(deepClone(originalFields));
  }, [originalSettings, originalFields]);

  // Load data on mount
  useEffect(() => {
    loadCdrSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    settingsData,
    originalSettings,
    fields,
    originalFields,
    isLoading,
    isSavingSettings,
    isSavingFields,
    hasGeneralChanges,
    hasFieldChanges,
    hasAnyChanges,
    error,
    loadCdrSettings,
    saveGeneralSettings,
    saveFieldSettings,
    saveAll,
    handleSettingsChange,
    handleFieldChange,
    handleAddField,
    handleRemoveField,
    handleFieldDragEnd,
    resetToOriginal,
    setFields,
  };
}

